import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, renderSlot as _renderSlot, resolveComponent as _resolveComponent, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "data-table" }
const _hoisted_2 = {
  key: 0,
  class: "data-table-container"
}
const _hoisted_3 = {
  key: 0,
  class: "table is-fullwidth"
}
const _hoisted_4 = { class: "column-heading" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = {
  key: 1,
  class: "data-table-column"
}
const _hoisted_7 = { key: 0 }
const _hoisted_8 = {
  key: 0,
  class: "default-cell-display"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { key: 1 }
const _hoisted_11 = {
  key: 0,
  class: "actions-column"
}
const _hoisted_12 = {
  key: 1,
  class: "loading-spinner-container"
}
const _hoisted_13 = { class: "loading-spinner" }

import PagingControl, { Page } from '@/shared/components/PagingControl.vue';
import DataTableColumn from '@/shared/components/dataTable/dataTableColumn';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import PageData from '@/shared/components/dataTable/pageData';
import DataTableIcon from '@/shared/components/dataTable/DataTableIcon.vue';
import { PropType, ref, watch, onMounted, useSlots } from 'vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'DataTable',
  props: {
  columns: {
    type: Array as PropType<DataTableColumn[]>,
    required: true,
  },
  data: {
    type: Array as PropType<any[]>,
    required: true,
  },
  total: {
    type: Number,
    default: 0,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Boolean,
    default: false,
  },
  rowsPerPage: {
    type: Number,
  },
  defaultSortBy: {
    type: String,
    default: 'asc',
  },
  defaultSortDirection: {
    type: String as PropType<'asc' | 'desc'>,
    default: '',
  },
},
  emits: ["load", "cellClicked"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const slots = useSlots();
const isLoading = ref(false);
const offset = ref(0);
const sortBy = ref<string>('');
const sortDirection = ref<'asc' | 'desc'>('asc');

watch(
  () => props.loading,
  (loading) => (isLoading.value = loading),
  { immediate: true },
);
watch(
  () => props.data,
  () => (isLoading.value = false),
  { immediate: true },
);

onMounted(() => {
  sortBy.value = props.defaultSortBy;
  sortDirection.value = props.defaultSortDirection;
});

function onPageChanged(page: Page) {
  offset.value = page.start;
  loadData();
}

function loadData() {
  isLoading.value = true;
  emit('load', {
    offset: offset.value,
    sortBy: sortBy.value,
    sortDirection: sortDirection.value,
  } as PageData);
}

function getSortClass(column: string) {
  if (column !== sortBy.value) {
    return '';
  } else {
    return sortDirection.value;
  }
}

function sortColumn(column: string) {
  if (column === sortBy.value) {
    if (sortDirection.value === 'desc') {
      sortDirection.value = 'asc';
    } else if (sortDirection.value === 'asc') {
      sortDirection.value = 'desc';
    }
  } else {
    sortDirection.value = 'desc';
    sortBy.value = column;
  }
  loadData();
}

function isCellClickable(row: any, clickableConfig?: boolean | ((row: any) => boolean)): boolean {
  if (typeof clickableConfig === 'function') {
    return clickableConfig(row);
  }
  return !!clickableConfig;
}

function cellClicked(row: any) {
  emit('cellClicked', row);
}

function hasResults() {
  if (props.pagination) {
    return props.total > 0 && props.data && props.data.length > 0;
  } else {
    return props.data && props.data.length > 0;
  }
}

function requiresAlignmentFix(icon: string) {
  return icon === 'DecimalIcon';
}

function getDisplayValue(row: any, column: DataTableColumn): string {
  if (column.formatter) {
    return column.formatter(row);
  }
  return row[column.name];
}

return (_ctx: any,_cache: any) => {
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (hasResults() || isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (hasResults() && !isLoading.value)
            ? (_openBlock(), _createElementBlock("table", _hoisted_3, [
                _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.columns, (column) => {
                      return (_openBlock(), _createElementBlock("th", {
                        key: column.name
                      }, [
                        _createElementVNode("div", _hoisted_4, [
                          (column.sortable)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: "data-table-column sortable",
                                onClick: ($event: any) => (sortColumn(column.name))
                              }, [
                                (column.icon)
                                  ? (_openBlock(), _createBlock(DataTableIcon, {
                                      key: 0,
                                      icon: column.icon,
                                      class: _normalizeClass({ 'icon--gap-fix': requiresAlignmentFix(column.icon) })
                                    }, null, 8, ["icon", "class"]))
                                  : _createCommentVNode("", true),
                                _createTextVNode(" " + _toDisplayString(column.display) + " ", 1),
                                _createElementVNode("div", {
                                  class: _normalizeClass(getSortClass(column.name))
                                }, null, 2)
                              ], 8, _hoisted_5))
                            : _createCommentVNode("", true),
                          (!column.sortable)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                (column.icon)
                                  ? (_openBlock(), _createBlock(DataTableIcon, {
                                      key: 0,
                                      icon: column.icon,
                                      class: _normalizeClass({ 'icon--gap-fix': requiresAlignmentFix(column.icon) })
                                    }, null, 8, ["icon", "class"]))
                                  : _createCommentVNode("", true),
                                _createTextVNode(" " + _toDisplayString(column.display), 1)
                              ]))
                            : _createCommentVNode("", true)
                        ])
                      ]))
                    }), 128)),
                    (!!_unref(slots)['actions'])
                      ? (_openBlock(), _createElementBlock("th", _hoisted_7))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createElementVNode("tbody", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.data, (row) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      class: "data-item",
                      key: row.id
                    }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.columns, (column) => {
                        return (_openBlock(), _createElementBlock("td", {
                          class: _normalizeClass(["data-cell", { 'cell-wrap': column.wrappable }]),
                          key: column.name
                        }, [
                          (!_unref(slots)[column.name])
                            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                (isCellClickable(row, column.clickable))
                                  ? (_openBlock(), _createElementBlock("a", {
                                      key: 0,
                                      class: "clickable-cell",
                                      onClick: ($event: any) => (cellClicked(row))
                                    }, _toDisplayString(getDisplayValue(row, column)), 9, _hoisted_9))
                                  : _createCommentVNode("", true),
                                (!isCellClickable(row, column.clickable))
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(getDisplayValue(row, column)), 1))
                                  : _createCommentVNode("", true)
                              ]))
                            : _createCommentVNode("", true),
                          (!!_unref(slots)[column.name])
                            ? _renderSlot(_ctx.$slots, column.name, {
                                key: 1,
                                row: row
                              })
                            : _createCommentVNode("", true)
                        ], 2))
                      }), 128)),
                      (!!_unref(slots)['actions'])
                        ? (_openBlock(), _createElementBlock("td", _hoisted_11, [
                            _renderSlot(_ctx.$slots, "actions", { row: row })
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  }), 128))
                ])
              ]))
            : _createCommentVNode("", true),
          (isLoading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createElementVNode("div", _hoisted_13, [
                  _createVNode(_component_n_loading_spinner)
                ])
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (__props.pagination && hasResults())
      ? (_openBlock(), _createBlock(PagingControl, {
          key: 1,
          class: "pagination-controls",
          onPageChanged: onPageChanged,
          total: __props.total,
          "rows-per-page": __props.rowsPerPage
        }, null, 8, ["total", "rows-per-page"]))
      : _createCommentVNode("", true),
    (!hasResults() && !isLoading.value)
      ? _renderSlot(_ctx.$slots, "nodata", { key: 2 })
      : _createCommentVNode("", true)
  ]))
}
}

})